import {
  BufferGeometry,
  Color,
  DataTexture,
  InterleavedBuffer,
  InterleavedBufferAttribute,
  ShaderMaterial,
  Vector3,
} from 'three';

import { CustomEventEmitter } from '@/package';
import type { PCDData } from '@/type';

import fragmentShader from './fragmentShader';
import type {
  MaterialByViewMode,
  PcdMangerEvent,
  ShaderMaterialUniform,
} from './type';
import { setMaterialUniform } from './util';
import vertexShader from './vertexShader';

class PcdManager extends CustomEventEmitter<PcdMangerEvent> {
  static instance: PcdManager;

  private _geometry = new BufferGeometry();
  private _material = new ShaderMaterial({
    uniforms: {
      uBoxHalfSizeList: {
        value: Array.from({ length: 1 }, () => new Vector3()),
      },
      uBoxColorList: {
        value: Array.from({ length: 1 }, () => new Color(0xffffff)),
      },
      uNumActiveBoxList: { value: 1 },
      uMatrixTexture: {
        value: new DataTexture(new Float32Array(0), 0, 0),
      },
      fMatrixTextureHeight: {
        value: 1,
      },
      fSize: { value: 1.5 },
      bFixedIntensity: { value: true },
      bIsMainView: { value: false },
      bPcdPointColorVisible: { value: true },
      bVisible: { value: true },
    },
    defines: {
      MAX_BOX_LIST: 1,
    },
    vertexShader,
    fragmentShader,
    vertexColors: true,
  });
  private _materialByViewMode!: MaterialByViewMode;

  constructor() {
    super();

    if (PcdManager.instance instanceof PcdManager) {
      return PcdManager.instance;
    }

    PcdManager.instance = this;

    this._materialByViewMode = {
      front: this._material.clone(),
      side: this._material.clone(),
      top: this._material.clone(),
    };
  }

  cleanup() {
    this._geometry.dispose();
    this._material.dispose();

    this._materialByViewMode.front.dispose();
    this._materialByViewMode.side.dispose();
    this._materialByViewMode.top.dispose();
  }

  getGeometry() {
    return this._geometry;
  }

  getMaterial(viewMode?: keyof MaterialByViewMode) {
    return !viewMode ? this._material : this._materialByViewMode[viewMode];
  }

  setGeometry({ pointList }: PCDData) {
    this.emit('beforeChangeGeometry');

    const geometry = this._geometry;
    const interleaved = new InterleavedBuffer(pointList, 4);

    geometry.setAttribute(
      'position',
      new InterleavedBufferAttribute(interleaved, 3, 0),
    );

    geometry.setAttribute(
      'intensity',
      new InterleavedBufferAttribute(interleaved, 1, 3),
    );

    this.emit('afterChangeGeometry', null, { debounceTime: 10 });
  }

  updateGeometry({ pointList }: PCDData) {
    const interleaved = new InterleavedBuffer(pointList, 4);

    this._geometry.setAttribute(
      'position',
      new InterleavedBufferAttribute(interleaved, 3, 0),
    );

    this._geometry.setAttribute(
      'intensity',
      new InterleavedBufferAttribute(interleaved, 1, 3),
    );

    this._geometry.computeBoundingBox();
    this._geometry.computeBoundingSphere();
  }

  setMaterialUniform({
    viewMode,
    uniformMap,
  }: {
    viewMode?: keyof MaterialByViewMode;
    uniformMap: Partial<ShaderMaterialUniform>;
  }) {
    setMaterialUniform(this.getMaterial(viewMode), uniformMap);
  }
}

const pcdManager = new PcdManager();

export default pcdManager;
